// Global Types
import type {
  $FullAdminRegatta,
  $FullRegattaEntry,
  $FullRegattaEntryRequirement,
  $Model,
} from 'common.data';
import type {
  $Language,
  $Translate,
} from 'common.language';

// Global Enums
import {
  GroupType,
  RatingType,
  RegattaRequirementType,
} from 'common.data';

// Global Enums
import {
  countryCodes,
  docRoute,
  docType,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  CardHeader,
  Col,
  Row,
  Table,
} from 'reactstrap';
import {
  AvInput,
} from 'availity-reactstrap-validation';
import {
  CommonDialog,
  DeleteDialog,
  FormDialog,
  InputCheckbox,
  TdDate,
  TdImage,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import {
  getImageUrl,
} from '@vroskus/library-helpers/dist/browser';
import {
  getRating,
  getRegattaEntryParams,
  getRegattaEntryRequirementValue,
  getRegattaEntryTitle,
} from 'common.helpers/dist/browser';
import {
  i18next,
} from 'common.language';
import {
  getDocumentUrl,
  publicBorder,
} from '../../../../helpers';
import config from '../../../../config';

// Components
import EmptyTable from '../../../common/EmptyTable';
import ThQty from '../../../common/ThQty';

// Types
import type {
  $RedirectAction,
} from '../../../../redux/modules/main/actions';
import type {
  $RegattaGroupBundle,
} from '../../../../types';

export type $OwnProps = $Translate & {
  item: $RegattaGroupBundle;
  onRegattaEntryChecksUpdate: (regattaEntryChecks: Array<$Model<'RegattaEntryCheck'>>) => void;
  onRegattaEntryDelete: (regattaEntry: $FullRegattaEntry) => void;
  onRegattaEntryEdit: (regattaEntry: $FullRegattaEntry) => void;
  regatta: $FullAdminRegatta;
};

export type $StateProps = Record<string, never>;

export type $DispatchProps = {
  redirect: $RedirectAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

class RegattaGroupSectionView extends React.Component<$Props> {
  /* eslint-disable-next-line complexity */
  renderRegattaGroupTableHead({
    hasTrakers,
  }: {
    hasTrakers: boolean;
  }) {
    const {
      item,
      regatta,
      t,
    } = this.props;

    return (
      <thead>
        <tr>
          {regatta.BowNoEnabled && (
            <th>
              {t('Bow no.')}
            </th>
          )}
          <th>
            {t('Image')}
          </th>
          <th>
            {regatta.CountryEnabled === true ? t('Country') : t('Reg. no.')}
          </th>
          {this.renderRegattaGroupTableHeadDiff()}
          <th>
            {t('Date created')}
          </th>
          {this.renderRegattaEntryChecksRequirementsTh()}
          {item.RatingType !== null && (
            <th>
              {`${t('Ratings')}: `}
              <span className={'text-muted'}>
                {item.RatingType}
              </span>
            </th>
          )}
          {item.PreliminaryCrewQuantityEnabled === true && (
            <th>
              {t('Preliminary crew size')}
            </th>
          )}
          {hasTrakers === true && (
            <th className={'text-center'}>
              {t('Tracker ID')}
            </th>
          )}
          <ThQty value={item.RegattaEntries.length} />
        </tr>
      </thead>
    );
  }

  renderRegattaGroupTableBody({
    hasTrakers,
  }: {
    hasTrakers: boolean;
  }) {
    const {
      item,
    } = this.props;

    return (
      <tbody>
        {item.RegattaEntries.map((
          dataItem: $FullRegattaEntry,
        ) => this.renderRegattaGroupTableBodyRow({
          dataItem,
          hasTrakers,
        }))}
      </tbody>
    );
  }

  renderRegattaRequirementsButton(dataItem: $FullRegattaEntry) {
    const {
      regatta,
      t,
    } = this.props;

    return (
      <CommonDialog
        content={(
          <Table className={'mb-0'}>
            <tbody>
              {dataItem.RegattaEntryRequirements.map((
                regattaEntryRequirement: $FullRegattaEntryRequirement,
              ) => (regattaEntryRequirement.RegattaRequirement ? (
                <tr key={regattaEntryRequirement.id}>
                  <td>
                    <div>
                      {regattaEntryRequirement.RegattaRequirement.Name}
                    </div>
                    <small className={'text-muted'}>
                      {regattaEntryRequirement.RegattaRequirement.Content}
                    </small>
                  </td>
                  <td>
                    {_.includes(
                      [
                        RegattaRequirementType.fileSource,
                        RegattaRequirementType.file,
                      ],
                      regattaEntryRequirement.RegattaRequirement.Type,
                    ) ? (
                      <Button
                        color={'secondary'}
                        href={getRegattaEntryRequirementValue({
                          cdnUrl: config.CDN_URL,
                          regattaEntryRequirement,
                        })}
                        id={'regatta-entry-requirement-file-button'}
                        outline
                        size={'md'}
                        target={'_blank'}
                      >
                        <em className={'far fa-file'} />
                      </Button>
                      ) : (
                        <span>
                          {getRegattaEntryRequirementValue({
                            cdnUrl: config.CDN_URL,
                            regattaEntryRequirement,
                          })}
                        </span>
                      )}
                  </td>
                </tr>
              ) : null))}
            </tbody>
          </Table>
        )}
        id={`regatta-entry-requirements-dialog-${dataItem.id}`}
        labelCloseButton={t('Close')}
        labelConfirmButton={t('Confirm')}
        subTitle={getRegattaEntryTitle({
          bowNoEnabled: regatta.BowNoEnabled,
          countryEnabled: regatta.CountryEnabled,
          regattaEntry: dataItem,
        })}
        title={t('Regatta entry requirements')}
      >
        <Button
          id={`regatta-entry-requirements-button-${dataItem.id}`}
          title={t('Regatta entry requirements')}
        >
          <em className={'icon-list'} />
        </Button>
      </CommonDialog>
    );
  }

  renderRegattaChecksButton(dataItem: $FullRegattaEntry) {
    const {
      onRegattaEntryChecksUpdate,
      regatta,
      t,
    } = this.props;

    const colorClass: string = _.filter(
      dataItem.RegattaEntryChecks,
      (item) => !item.Value,
    ).length > 0 ? 'warning' : 'success';

    return (
      <FormDialog
        fields={(
          <>
            {dataItem.RegattaEntryChecks.map((
              regattaEntryCheck: $Model<'RegattaEntryCheck'>,
              index: number,
            ) => (
              <Row key={regattaEntryCheck.id}>
                <Col>
                  <AvInput
                    name={`RegattaEntryChecks[${index}]id`}
                    style={{
                      display: 'none',
                    }}
                    value={regattaEntryCheck.id}
                  />
                  <AvInput
                    name={`RegattaEntryChecks[${index}]RegattaEntryId`}
                    style={{
                      display: 'none',
                    }}
                    value={regattaEntryCheck.RegattaEntryId}
                  />
                  <InputCheckbox
                    label={_.get(
                      _.find(
                        regatta.RegattaChecks,
                        {
                          id: regattaEntryCheck.RegattaCheckId,
                        },
                      ),
                      'Name',
                      '',
                    )}
                    name={`RegattaEntryChecks[${index}]Value`}
                    value={regattaEntryCheck.Value}
                  />
                </Col>
              </Row>
            ))}
          </>
        )}
        id={`update-regatta-entry-check-dialog-${dataItem.id}`}
        labelCloseButton={t('Close')}
        labelSaveButton={t('Save')}
        onAction={async (values) => onRegattaEntryChecksUpdate(
          values.RegattaEntryChecks as Array<$Model<'RegattaEntryCheck'>>,
        )}
        subTitle={getRegattaEntryTitle({
          bowNoEnabled: regatta.BowNoEnabled,
          countryEnabled: regatta.CountryEnabled,
          regattaEntry: dataItem,
        })}
        title={t('Regatta entry checks')}
      >
        <Button
          className={'mr-1'}
          id={`update-regatta-entry-check-button-${dataItem.id}`}
          title={t('Regatta entry checks')}
        >
          <em className={`icon-check text-${colorClass}`} />
        </Button>
      </FormDialog>
    );
  }

  renderRegattaEntryChecksRequirementsButton(dataItem: $FullRegattaEntry) {
    const {
      regatta,
    } = this.props;

    const checks: boolean = regatta.RegattaChecks.length > 0;
    const requirements: boolean = regatta.RegattaRequirements.length > 0;

    if ([checks, requirements].indexOf(true) !== -1) {
      return (
        <td className={'text-nowrap'}>
          {checks && this.renderRegattaChecksButton(dataItem)}
          {requirements && this.renderRegattaRequirementsButton(dataItem)}
        </td>
      );
    }

    return null;
  }

  renderRegattaGroupHeader() {
    const {
      item,
      t,
    } = this.props;

    const {
      RegattaGroupId,
    } = item;

    return (
      <CardHeader>
        <Row>
          <Col>
            <span>{item.Name}</span>
          </Col>
          {RegattaGroupId !== null && (
            <Col>
              <div className={'d-flex justify-content-end'}>
                <Button
                  className={'mr-1'}
                  color={'secondary'}
                  href={getDocumentUrl({
                    cdnUrl: config.CDN_URL,
                    docRoute: docRoute.adminRegattaEntryEmpty,
                    docType: docType.pdf,
                    id: RegattaGroupId,
                    language: i18next.language as $Language,
                  })}
                  id={'regatta-entry-file-button'}
                  outline
                  size={'sm'}
                  target={'_blank'}
                >
                  <em className={'far fa-file-pdf mr-1'} />
                  {t('Empty entry form')}
                </Button>
                <Button
                  color={'secondary'}
                  href={getDocumentUrl({
                    cdnUrl: config.CDN_URL,
                    docRoute: docRoute.adminRegattaGroupEntries,
                    docType: docType.pdf,
                    id: RegattaGroupId,
                    language: i18next.language as $Language,
                  })}
                  id={'regatta-group-entries-list-file-button'}
                  outline
                  size={'sm'}
                  target={'_blank'}
                >
                  <em className={'far fa-file-pdf mr-1'} />
                  {t('Group entries list')}
                </Button>
              </div>
            </Col>
          )}
        </Row>
      </CardHeader>
    );
  }

  renderRegattaEntryButtons(regattaEntry: $FullRegattaEntry) {
    const {
      onRegattaEntryDelete,
      onRegattaEntryEdit,
      regatta,
      t,
    } = this.props;

    return (
      <div className={'d-flex justify-content-end'}>
        <Button
          className={'mr-1'}
          color={'secondary'}
          href={getDocumentUrl({
            cdnUrl: config.CDN_URL,
            docRoute: docRoute.adminRegattaEntry,
            docType: docType.pdf,
            id: regattaEntry.id,
            language: i18next.language as $Language,
          })}
          id={`regatta-entry-file-${regattaEntry.id}`}
          outline
          size={'sm'}
          target={'_blank'}
        >
          <em className={'far fa-file-pdf'} />
        </Button>
        <Button
          className={'mr-1'}
          color={'warning'}
          id={`update-regatta-entry-button-${regattaEntry.id}`}
          onClick={() => onRegattaEntryEdit(regattaEntry)}
          outline
          size={'sm'}
        >
          {t('Edit')}
        </Button>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-regatta-entry-dialog-${regattaEntry.id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => onRegattaEntryDelete(regattaEntry)}
          subTitle={getRegattaEntryTitle({
            bowNoEnabled: regatta.BowNoEnabled,
            countryEnabled: regatta.CountryEnabled,
            regattaEntry,
          })}
          title={t('Regatta entry')}
        >
          <Button
            color={'danger'}
            id={`delete-regatta-entry-button-${regattaEntry.id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderRegattaEntryChecksRequirementsTh() {
    const {
      regatta,
      t,
    } = this.props;

    const labels: Array<string> = [];

    if (regatta.RegattaChecks.length > 0) {
      labels.push(t('Checks'));
    }

    if (regatta.RegattaRequirements.length > 0) {
      labels.push(t('Reqs.'));
    }

    if (labels.length > 0) {
      return (
        <th>
          {labels.join(' / ')}
        </th>
      );
    }

    return null;
  }

  renderRegattaGroupTableHeadDiff() {
    const {
      item,
      t,
    } = this.props;

    return (item.GroupType === GroupType.sport ? (
      <>
        <th>{[t('Skipper'), t('Crew')].join(' / ')}</th>
        <th>{t('City')}</th>
        <th>{t('Club')}</th>
        {item.CoachEnabled === true && (
          <th>{t('Coach')}</th>
        )}
      </>
    ) : (
      <>
        <th>{[t('Title'), t('Skipper')].join(' / ')}</th>
        <th>{t('Make / Model')}</th>
      </>
    ));
  }

  renderRegattaGroupTableBodyRowTitle({
    dataItem,
  }: {
    dataItem: $FullRegattaEntry;
  }) {
    const {
      regatta,
      t,
    } = this.props;

    const {
      countryAlpha3,
      regNo,
      sailNo,
    } = getRegattaEntryParams({
      bowNoEnabled: regatta.BowNoEnabled,
      countryEnabled: regatta.CountryEnabled,
      regattaEntry: dataItem,
    });

    return (
      regatta.CountryEnabled === true ? (
        <>
          <div className={'d-flex flex-row align-items-center'}>
            <img
              alt={regNo}
              className={'mr-1'}
              src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${countryCodes[countryAlpha3] || ''}.svg`}
              style={{
                height: 12,
              }}
            />
            <span>{countryAlpha3}</span>
          </div>
          <small className={'text-muted'}>
            {regNo}
          </small>
        </>
      ) : (
        <>
          <div title={t('Reg. no.')}>
            {regNo}
          </div>
          {sailNo !== null && (
            <small className={'text-muted'}>
              {sailNo}
            </small>
          )}
        </>
      )
    );
  }

  /* eslint-disable-next-line complexity */
  renderRegattaGroupTableBodyRow({
    dataItem,
    hasTrakers,
  }: {
    dataItem: $FullRegattaEntry;
    hasTrakers: boolean;
  }) {
    const {
      item,
      regatta,
    } = this.props;

    const skipperName: string = _.get(
      dataItem,
      'RegattaEntryCrewmembers[0].Name',
    );

    const crewmemberNames: string = _.map(
      _.tail(dataItem.RegattaEntryCrewmembers),
      'Name',
    ).join(', ');

    return (regatta !== null ? (
      <tr
        key={dataItem.id}
        style={publicBorder(dataItem.EntryId !== null)}
      >
        {regatta.BowNoEnabled && (
          <td>{dataItem.BowNo}</td>
        )}
        <TdImage
          largeImageUrl={getImageUrl({
            cdnUrl: config.CDN_URL,
            image: dataItem.Image,
            size: 'max',
          })}
          noImageUrl={getImageUrl({
            cdnUrl: config.CDN_URL,
            image: null,
            size: 'min',
          })}
          smallImageUrl={getImageUrl({
            cdnUrl: config.CDN_URL,
            image: dataItem.Image,
            size: 'min',
          })}
          value={dataItem.Image}
        />
        <td className={'lh-1 py-1'}>
          {this.renderRegattaGroupTableBodyRowTitle({
            dataItem,
          })}
        </td>
        {item.GroupType === GroupType.sport ? (
          <>
            <td className={'lh-1 py-1'}>
              <div>
                {skipperName}
              </div>
              {dataItem.RegattaEntryCrewmembers.length > 1 && (
                <small
                  className={'text-muted'}
                  title={crewmemberNames}
                >
                  {crewmemberNames}
                </small>
              )}
            </td>
            <td>
              {dataItem.Harbor}
            </td>
            <td>
              {dataItem.Club}
            </td>
            {item.CoachEnabled === true && (
            <td>
              {dataItem.CoachName}
            </td>
            )}
          </>
        ) : (
          <>
            <td className={'lh-1 py-1'}>
              <div>
                {dataItem.Name}
              </div>
              <small
                className={'text-muted'}
                title={crewmemberNames}
              >
                {`${skipperName} +${dataItem.RegattaEntryCrewmembers.length - 1}`}
              </small>
            </td>
            <td>{dataItem.Model}</td>
          </>
        )}
        <TdDate
          duration
          multiple
          value={dataItem.createdAt}
        />
        {this.renderRegattaEntryChecksRequirementsButton(dataItem)}
        {item.RatingType !== null && (
          <td>
            {this.renderRatingsButton(
              item.RatingType,
              dataItem,
            )}
          </td>
        )}
        {item.PreliminaryCrewQuantityEnabled === true && (
          <td>
            {dataItem.PreliminaryCrewQuantity}
          </td>
        )}
        {hasTrakers === true && (
          <td className={'text-center'}>
            {dataItem.TrackerId}
          </td>
        )}
        <td>
          {this.renderRegattaEntryButtons(dataItem)}
        </td>
      </tr>
    ) : null);
  }

  renderRegattaGroupTable() {
    const {
      item,
    } = this.props;

    const hasTrakers: boolean = item.RegattaEntries.filter((
      dataItem: $FullRegattaEntry,
    ) => (dataItem.TrackerId !== null)).length > 0;

    return (
      <Table responsive>
        {this.renderRegattaGroupTableHead({
          hasTrakers,
        })}
        {this.renderRegattaGroupTableBody({
          hasTrakers,
        })}
      </Table>
    );
  }

  /* eslint-disable-next-line complexity */
  renderRatingsButton(type: null | RatingType, item: $FullRegattaEntry) {
    const {
      redirect,
      t,
    } = this.props;

    let rating: $Model<'Rating'> | null = null;

    if (type !== null) {
      rating = getRating({
        ratings: item.Ratings,
        type,
      }) || null;
    }

    return (
      <Button
        color={'secondary'}
        id={`redirect-regatta-entry-ratings-page-button-${item.id}`}
        onClick={() => redirect({
          pathname: `/regatta/entry/${item.id}/ratings`,
        })}
        size={'sm'}
      >
        {rating !== null ? (
          <span title={rating.KeyValueName}>
            <em className={`mr-1 fas fa-circle ${rating.Latest ? 'text-success' : 'text-warning'}`} />
            {rating.KeyValue || ''}
          </span>
        ) : (
          <span>
            <em className={'mr-1 fas fa-circle text-danger'} />
            {t('No rating')}
          </span>
        )}
      </Button>
    );
  }

  render() {
    const {
      item,
    } = this.props;

    return (
      <Card>
        {this.renderRegattaGroupHeader()}
        {item.RegattaEntries.length === 0 ? (
          <EmptyTable />
        ) : this.renderRegattaGroupTable()}
      </Card>
    );
  }
}

export default RegattaGroupSectionView;
